import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable, Subject, forkJoin, of } from 'rxjs';
import { auditTime, catchError, switchMap } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { NachrichtenThreadSummary, unreadThreadIdSet } from './nachrichten-unread';

export interface NachrichtenUnreadCounts {
  total: number;
  markt: number;
  hilfe: number;
}

export interface NachrichtenThread {
  threadId: string;
  postId: string;
  postType: string;
  direction: string;
  hilfeType?: string;
  postTitle: string;
  otherName: string;
  otherUserId?: string;
  lastMessage?: string;
  lastMessageAt?: string;
  unread: boolean;
}

export interface NachrichtenCommentThread {
  postType: string;
  postId: string;
  postTitle: string;
  latestCommentText: string;
  latestCommentAt: string;
  latestCommenterName: string;
  unread: boolean;
}

const EMPTY_COUNTS: NachrichtenUnreadCounts = { total: 0, markt: 0, hilfe: 0 };

@Injectable({ providedIn: 'root' })
export class NachrichtenService {
  private http = inject(HttpClient);
  private base = environment.apiUrl;

  private readonly unreadCountsSubject = new BehaviorSubject<NachrichtenUnreadCounts>(EMPTY_COUNTS);
  readonly unreadCounts$: Observable<NachrichtenUnreadCounts> =
    this.unreadCountsSubject.asObservable();

  private readonly unreadThreadIdsSubject = new BehaviorSubject<Set<string>>(new Set());
  readonly unreadThreadIds$: Observable<Set<string>> = this.unreadThreadIdsSubject.asObservable();

  private readonly refreshTrigger = new Subject<void>();

  constructor() {
    this.refreshTrigger
      .pipe(
        auditTime(100),
        switchMap(() =>
          forkJoin([
            this.http
              .get<NachrichtenUnreadCounts>(`${this.base}/api/nachrichten/unread-counts`)
              .pipe(catchError(() => of(EMPTY_COUNTS))),
            this.http
              .get<NachrichtenThreadSummary[]>(`${this.base}/api/nachrichten`)
              .pipe(catchError(() => of([] as NachrichtenThreadSummary[]))),
          ]),
        ),
      )
      .subscribe(([counts, threads]) => {
        this.unreadCountsSubject.next(counts);
        this.unreadThreadIdsSubject.next(unreadThreadIdSet(threads));
      });
  }

  refreshUnreadCounts(): void {
    this.refreshTrigger.next();
  }

  markRead(threadId: string): void {
    this.http
      .post<void>(`${this.base}/api/nachrichten/${encodeURIComponent(threadId)}/read`, null)
      .pipe(catchError(() => of(undefined)))
      .subscribe(() => this.refreshTrigger.next());
  }

  markReadByPost(postType: string, postId: string): void {
    this.http
      .post<void>(`${this.base}/api/nachrichten/read-by-post/${postType}/${postId}`, null)
      .pipe(catchError(() => of(undefined)))
      .subscribe(() => this.refreshTrigger.next());
  }

  getThreads(): Observable<NachrichtenThread[]> {
    return this.http
      .get<NachrichtenThread[]>(`${this.base}/api/nachrichten`)
      .pipe(catchError(() => of([] as NachrichtenThread[])));
  }

  getCommentThreads(): Observable<NachrichtenCommentThread[]> {
    return this.http
      .get<NachrichtenCommentThread[]>(`${this.base}/api/nachrichten/comment-threads`)
      .pipe(catchError(() => of([] as NachrichtenCommentThread[])));
  }

  markCommentThreadRead(postType: string, postId: string): void {
    this.http
      .post<void>(
        `${this.base}/api/nachrichten/comment-threads/${encodeURIComponent(postType)}/${encodeURIComponent(postId)}/read`,
        null,
      )
      .pipe(catchError(() => of(undefined)))
      .subscribe(() => this.refreshTrigger.next());
  }
}
